import { useCallback, useState } from "react";
import {
  hasReservedXmlTag,
  stripControlChars,
} from "@school-sales-ai/shared/security";
import { KB_MAX_CHARS, KB_MIN_CHARS } from "@school-sales-ai/shared/schemas";

export { KB_MAX_CHARS, KB_MIN_CHARS };

export type KbDraft = {
  content: string;
  hasEditedOnce: boolean;
  dirty: boolean;
  tooShort: boolean;
  tooLong: boolean;
  invalid: boolean;
  /** Server-side rejection the user would otherwise only meet as a 400. */
  problem: string | null;
  onEditorChange: (markdown: string) => void;
  /** Call after a successful save: the current document becomes the baseline. */
  markSaved: () => void;
  /** Call when discarding: the editor is what actually resets, this follows it. */
  reset: (saved: string) => void;
  /** Call when handing the editor a document it did not produce — see below. */
  seed: (document: string, saved: string) => void;
};

/**
 * Owns the knowledge base draft.
 *
 * The one thing worth knowing: the editor re-serializes the document, so the
 * markdown it hands back on mount is usually not byte-identical to what the API
 * returned (`- ` for bullets, `*text*` for emphasis, and so on). Comparing
 * against the API's copy would mark the KB dirty before anyone typed, arming the
 * unsaved-changes blocker on a page the user only looked at.
 *
 * **The editor never reports the document it was mounted with.** `onChange` fires for
 * what the user types and nothing else — verified against MDXEditor 4.2.4 in
 * `KbEditor.contract.test.tsx`. So any document that arrives from outside (a generated
 * draft, the saved knowledge base) has to be pushed in here with `seed` as well as into
 * the editor. Skipping that leaves this hook believing the document is empty: 0
 * characters, "too short to save", and a disabled button over a full screen of text.
 *
 * **Invisible characters are removed, not refused.** Pasting from Word or Google Docs
 * into a contenteditable carries zero-width and bidi marks the API rejects
 * (`updateKnowledgeBaseSchema`). Every document entering this hook goes through
 * `stripControlChars`, so `content` — what both editors save — never holds one. The
 * editor's own DOM may still contain them; they are invisible there and never sent.
 */
export function useKbDraft(): KbDraft {
  const [content, setContent] = useState("");
  const [baseline, setBaseline] = useState<string | null>(null);
  const [hasEditedOnce, setHasEditedOnce] = useState(false);

  const onEditorChange = useCallback((raw: string) => {
    const markdown = stripControlChars(raw);
    setContent(markdown);
    setBaseline((current) => {
      if (current === null) return markdown; // mount
      if (markdown !== current) setHasEditedOnce(true);
      return current;
    });
  }, []);

  const markSaved = useCallback(() => {
    setBaseline(content);
    setHasEditedOnce(false);
  }, [content]);

  const reset = useCallback((raw: string) => {
    const saved = stripControlChars(raw);
    setContent(saved);
    setBaseline(saved);
    setHasEditedOnce(false);
  }, []);

  /**
   * Adopts a document the editor was handed rather than one it produced.
   *
   * `saved` is kept as the baseline instead of `document`, so a generated draft nobody
   * has saved reads as unsaved changes — which is what arms the navigation blocker.
   * Seeding the saved knowledge base back to itself (`document === saved`) therefore
   * reads as clean, exactly as it should.
   */
  const seed = useCallback((document: string, saved: string) => {
    setContent(stripControlChars(document));
    setBaseline(stripControlChars(saved));
    setHasEditedOnce(false);
  }, []);

  const tooShort = content.trim().length < KB_MIN_CHARS;
  const tooLong = content.length > KB_MAX_CHARS;

  // The API rejects these (updateKnowledgeBaseSchema). Caught here, they get a
  // sentence instead of a bare 400. Unlike invisible characters they are visible
  // text the person wrote, so they are refused rather than silently removed.
  const problem: string | null = hasReservedXmlTag(content)
    ? "El texto contiene etiquetas reservadas por el sistema y no puede guardarse."
    : null;

  return {
    content,
    hasEditedOnce,
    dirty: baseline !== null && content !== baseline,
    tooShort,
    tooLong,
    invalid: tooShort || tooLong || problem !== null,
    problem,
    onEditorChange,
    markSaved,
    reset,
    seed,
  };
}
